import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  LIST_EXERCISES,
  CREATE_EXERCISE,
  UPDATE_EXERCISE,
  DELETE_EXERCISE,
  LIST_GOALS,
  GET_ALL_SUB_CATEGORIES,
  GET_ALL_LEVELS
} from '../graphql/exerciseOperations';
import { LIST_TRAININGS } from '../graphql/trainingOperations';
import {
  TrashIcon,
  PencilIcon,
  PlusIcon,
  XMarkIcon,
  PlayIcon,
  FunnelIcon,
  ArrowPathIcon
} from '@heroicons/react/24/solid';
import { handleGraphQLError, formatDate } from '../utils/errorHandler';
import { toast } from 'react-toastify';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';

const ExercisesManagement = () => {
  const { isDarkMode } = useTheme();

  // Form states
  const [exerciseData, setExerciseData] = useState({
    name: null,
    goalId: null,
    subCategoryId: null,
    levelId: null,
    trainingId: null,
    description: null,
    length: null,
    trending: null,
    forYou: null,
    tags: null
  });
  const [editingExercise, setEditingExercise] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);

  // Fetch exercises
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [filters, setFilters] = useState({
    name: null,
    goalId: null,
    subCategoryId: null,
    levelId: null,
    length: null,
    trending: null,
    forYou: null,
    tags: null
  });

  // Fetch levels for level filter dropdown
  const { data: levelsData } = useQuery(GET_ALL_LEVELS);

  // Fetch exercises with pagination and filters
  const { loading, error, data, refetch } = useQuery(LIST_EXERCISES, {
    variables: {
      page,
      limit,
      filters: Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== null)
      )
    },
    onError: (error) => handleGraphQLError(error, 'Failed to load exercises')
  });

  // Fetch goals
  const { data: goalsData } = useQuery(LIST_GOALS, {
    onError: (error) => handleGraphQLError(error, 'Failed to load goals')
  });

  // Fetch trainings
  const { data: trainingsData } = useQuery(LIST_TRAININGS, {
    variables: { page: 1, limit: 100 },
    onError: (error) => handleGraphQLError(error, 'Failed to load trainings')
  });

  const { data: subCategoriesData } = useQuery(GET_ALL_SUB_CATEGORIES);

  // Create Exercise Mutation
  const [createExercise] = useMutation(CREATE_EXERCISE, {
    onCompleted: (data) => {
      const createdExercise = data.createExercise;

      // Trigger file upload after exercise creation
      handleFileUpload(createdExercise.id);

      setIsCreateModalOpen(false);
      setExerciseData({
        name: null,
        goalId: null,
        subCategoryId: null,
        levelId: null,
        trainingId: null,
        description: null,
        length: null,
        trending: null,
        forYou: null,
        tags: null
      });

      // Reset file inputs
      setImageFile(null);
      setVideoFile(null);

      refetch();
      toast.success('Exercise created successfully');
    },
    onError: (error) => handleGraphQLError(error, 'Failed to create exercise')
  });

  // Update Exercise Mutation
  const [updateExercise] = useMutation(UPDATE_EXERCISE, {
    onCompleted: () => {
      setEditingExercise(null);
      setExerciseData({
        name: null,
        goalId: null,
        subCategoryId: null,
        levelId: null,
        trainingId: null,
        description: null,
        length: null,
        trending: null,
        forYou: null,
        tags: null
      });
      refetch();
      toast.success('Exercise updated successfully');
    },
    onError: (error) => handleGraphQLError(error, 'Failed to update exercise')
  });

  // Delete Exercise Mutation
  const [deleteExercise] = useMutation(DELETE_EXERCISE, {
    onCompleted: () => {
      refetch();
      toast.success('Exercise deleted successfully');
    },
    onError: (error) => handleGraphQLError(error, 'Failed to delete exercise')
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    const requiredFields = ['name'];
    const missingFields = requiredFields.filter(field => exerciseData[field] === null || exerciseData[field] === '');

    if (missingFields.length > 0) {
      toast.error(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return;
    }

    const input = {
      name: exerciseData.name,
      goalId: exerciseData.goalId,
      subCategoryId: exerciseData.subCategoryId,
      levelId: exerciseData.levelId,
      trainingId: exerciseData.trainingId,
      description: exerciseData.description,
      length: exerciseData.length ? parseInt(exerciseData.length, 10) : null,
      trending: exerciseData.trending,
      forYou: exerciseData.forYou,
      tags: exerciseData.tags
    };

    if (editingExercise) {
      updateExercise({
        variables: {
          updateExerciseId: editingExercise.id,
          input: input
        }
      });
    } else {
      createExercise({
        variables: { input }
      });
    }
  };

  const handleEdit = (exercise) => {
    setEditingExercise(exercise);
    setExerciseData({
      name: exercise.name || null,
      goalId: exercise.goalId || null,
      subCategoryId: exercise.subCategoryId || null,
      levelId: exercise.levelId || null,
      trainingId: exercise.trainingId || null,
      description: exercise.description || null,
      length: exercise.length || null,
      trending: exercise.trending || null,
      forYou: exercise.forYou || null,
      tags: exercise.tags || null
    });
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this exercise?')) {
      deleteExercise({
        variables: { deleteExerciseId: id }
      });
    }
  };

  const openCreateModal = () => {
    setIsCreateModalOpen(true);
    setExerciseData({
      name: null,
      goalId: null,
      subCategoryId: null,
      levelId: null,
      trainingId: null,
      description: null,
      length: null,
      trending: null,
      forYou: null,
      tags: null
    });
    setEditingExercise(null);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
    setExerciseData({
      name: null,
      goalId: null,
      subCategoryId: null,
      levelId: null,
      trainingId: null,
      description: null,
      length: null,
      trending: null,
      forYou: null,
      tags: null
    });
    setEditingExercise(null);
  };

  const handleFileUpload = async (exerciseId) => {
    try {
      // Prepare form data
      const formData = new FormData();
      formData.append('exerciseId', exerciseId);

      // Append image if selected
      if (imageFile) {
        formData.append('image', imageFile);
      }

      // Append video if selected
      if (videoFile) {
        formData.append('video', videoFile);
      }

      // Only proceed if files are selected
      if (imageFile || videoFile) {
        const baseUrl = process.env.REACT_APP_API_BASE_URL;
        const uploadUrl = `${baseUrl}/uploadImage`;
        const response = await axios.post(uploadUrl, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        // Optional: handle successful upload
        toast.success('Files uploaded successfully');
      }
    } catch (error) {
      console.error('File upload error:', error);
      toast.error('Failed to upload files');
    }
  };

  const handleVideoClick = (videoUrl) => {
    setSelectedVideo(videoUrl);
  };

  const closeVideoModal = () => {
    setSelectedVideo(null);
  };

  // Pagination handlers
  const handleNextPage = () => {
    if (data?.getAllExercises.page < data?.getAllExercises.totalPages) {
      setPage(prevPage => prevPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (page > 1) {
      setPage(prevPage => prevPage - 1);
    }
  };

  // Filter change handler
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prevFilters => ({
      ...prevFilters,
      [name]: value || null
    }));
    // Reset to first page when filters change
    setPage(1);
  };

  // Additional filter inputs
  const handleBooleanFilterChange = (name, value) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [name]: value
    }));
    // Reset to first page when filters change
    setPage(1);
  };

  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      name: null,
      goalId: null,
      subCategoryId: null,
      levelId: null,
      length: null,
      trending: null,
      forYou: null,
      tags: null
    });
    setPage(1);
  };

  // Apply filters
  const handleApplyFilters = () => {
    // Trigger refetch with current filters
    refetch({
      page,
      limit,
      filters: Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== null)
      )
    });
  };

  // Loading and error states
  if (loading) return (
    <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
      Loading exercises...
    </div>
  );

  if (error) return (
    <div className={`text-center py-8 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`}>
      Error loading exercises
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Create New Exercise Button */}
      <div className="flex justify-end mb-4">
        <button
          onClick={openCreateModal}
          className={`
            flex items-center px-4 py-2 rounded-lg text-white font-semibold
            transition-all duration-300
            ${isDarkMode
              ? 'bg-blue-600 hover:bg-blue-700'
              : 'bg-blue-500 hover:bg-blue-600'
            }
          `}
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Create New Exercise
        </button>
      </div>

      {/* Filters */}
      <div className="grid md:grid-cols-5 gap-4 mb-4 items-end">
        <input
          name="name"
          value={filters.name || ''}
          onChange={handleFilterChange}
          placeholder="Filter by Name"
          className={`
            w-full px-4 py-2.5 
            ${isDarkMode
              ? 'bg-gray-700 text-gray-100 border-gray-600'
              : 'bg-white text-gray-900 border-gray-300'
            }
            border rounded-md 
            focus:outline-none focus:ring-2 
            ${isDarkMode
              ? 'focus:ring-blue-500 focus:border-blue-500'
              : 'focus:ring-blue-400 focus:border-blue-400'
            }
            transition duration-300
          `}
        />
        <select
          name="levelId"
          value={filters.levelId || ''}
          onChange={handleFilterChange}
          className={`
            w-full px-4 py-2.5 
            ${isDarkMode
              ? 'bg-gray-700 text-gray-100 border-gray-600'
              : 'bg-white text-gray-900 border-gray-300'
            }
            border rounded-md 
          `}
        >
          <option value="">Filter by Level</option>
          {levelsData?.getAllLevels?.map(level => (
            <option key={level.id} value={level.id}>
              {level.name}
            </option>
          ))}
        </select>
        <select
          name="goalId"
          value={filters.goalId || ''}
          onChange={handleFilterChange}
          className={`
            w-full px-4 py-2.5 
            ${isDarkMode
              ? 'bg-gray-700 text-gray-100 border-gray-600'
              : 'bg-white text-gray-900 border-gray-300'
            }
            border rounded-md 
          `}
        >
          <option value="">Filter by Goal</option>
          {goalsData?.listGoals?.goals?.map(goal => (
            <option key={goal.id} value={goal.id}>
              {goal.name}
            </option>
          ))}
        </select>
        <select
          name="subCategoryId"
          value={filters.subCategoryId || ''}
          onChange={handleFilterChange}
          className={`
            w-full px-4 py-2.5 
            ${isDarkMode
              ? 'bg-gray-700 text-gray-100 border-gray-600'
              : 'bg-white text-gray-900 border-gray-300'
            }
            border rounded-md 
          `}
        >
          <option value="">Filter by Sub Category</option>
          {subCategoriesData?.getAllSubCategories?.map(subCategory => (
            <option key={subCategory.id} value={subCategory.id}>
              {subCategory.name}
            </option>
          ))}
        </select>
        <div className="flex space-x-2">
          <button
            onClick={handleApplyFilters}
            className={`
              w-full px-4 py-2.5 rounded-md
              ${isDarkMode
                ? 'bg-blue-600 text-white hover:bg-blue-500'
                : 'bg-blue-500 text-white hover:bg-blue-600'
              }
              transition duration-300
              flex items-center justify-center
              space-x-2
            `}
          >
            <FunnelIcon className="h-5 w-5" />
            <span>Apply Filters</span>
          </button>
          <button
            onClick={handleResetFilters}
            className={`
              w-full px-4 py-2.5 rounded-md
              ${isDarkMode
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }
              transition duration-300
              flex items-center justify-center
              space-x-2
            `}
          >
            <ArrowPathIcon className="h-5 w-5" />
            <span>Reset</span>
          </button>
        </div>
      </div>

      {/* Create/Edit Exercise Modal */}
      {(isCreateModalOpen || editingExercise) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto">
          <div
            className={`
              w-full max-w-2xl max-h-[90vh] my-8
              ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}
              rounded-2xl shadow-2xl border 
              transition-colors duration-300 overflow-y-auto
              ${editingExercise
                ? 'ring-4 ring-blue-500/20'
                : 'ring-4 ring-green-500/20'
              }
            `}
          >
            <div className={`
              px-6 py-4 
              ${isDarkMode ? 'border-b-gray-700' : 'border-b-gray-200'}
              border-b 
              flex justify-between items-center
            `}>
              <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                {editingExercise ? 'Edit Exercise' : 'Create New Exercise'}
              </h2>
              <button
                onClick={closeCreateModal}
                className={`
                  ${isDarkMode
                    ? 'text-gray-400 hover:text-gray-200'
                    : 'text-gray-500 hover:text-gray-700'
                  } 
                  transition
                `}
                title="Cancel"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className={`
                    block text-sm font-medium mb-2
                    ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
                  `}>
                    Exercise Name *
                  </label>
                  <input
                    type="text"
                    value={exerciseData.name || ''}
                    onChange={(e) => setExerciseData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter exercise name"
                    required
                    className={`
                      w-full px-4 py-2.5 
                      ${isDarkMode
                        ? 'bg-gray-700 border-gray-600 text-gray-100 focus:ring-blue-400'
                        : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500'
                      }
                      border rounded-lg 
                      focus:outline-none focus:ring-2 
                      transition-colors duration-300
                    `}
                  />
                </div>

                <div>
                  <label className={`
                    block text-sm font-medium mb-2
                    ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
                  `}>
                    Goal
                  </label>
                  <select
                    value={exerciseData.goalId}
                    onChange={(e) => setExerciseData(prev => ({ ...prev, goalId: e.target.value }))}
                    className={`
                      w-full px-4 py-2.5 
                      ${isDarkMode
                        ? 'bg-gray-700 text-gray-100 border-gray-600'
                        : 'bg-white text-gray-900 border-gray-300'
                      }
                      border rounded-md 
                      focus:outline-none focus:ring-2 
                      ${isDarkMode
                        ? 'focus:ring-blue-500 focus:border-blue-500'
                        : 'focus:ring-blue-400 focus:border-blue-400'
                      }
                      transition duration-300
                    `}
                  >
                    <option value="">Select Goal</option>
                    {goalsData?.listGoals?.goals?.map(goal => (
                      <option key={goal.id} value={goal.id}>
                        {goal.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className={`
                    block text-sm font-medium mb-2
                    ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
                  `}>
                    Training
                  </label>
                  <select
                    value={exerciseData.trainingId || ''}
                    onChange={(e) => setExerciseData(prev => ({ ...prev, trainingId: e.target.value }))}
                    className={`
                      w-full px-4 py-2.5 
                      ${isDarkMode
                        ? 'bg-gray-700 text-gray-100 border-gray-600'
                        : 'bg-white text-gray-900 border-gray-300'
                      }
                      border rounded-md 
                      focus:outline-none focus:ring-2 
                      ${isDarkMode
                        ? 'focus:ring-blue-500 focus:border-blue-500'
                        : 'focus:ring-blue-400 focus:border-blue-400'
                      }
                      transition duration-300
                    `}
                  >
                    <option value="">Select Training</option>
                    {trainingsData?.listTrainings?.trainings?.map(training => (
                      <option key={training.id} value={training.id}>
                        {training.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`
                    block text-sm font-medium mb-2
                    ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
                  `}>
                    Sub Category
                  </label>
                  <select
                    value={exerciseData.subCategoryId}
                    onChange={(e) => setExerciseData(prev => ({ ...prev, subCategoryId: e.target.value }))}
                    className={`
                      w-full px-4 py-2.5 
                      ${isDarkMode
                        ? 'bg-gray-700 border-gray-600 text-gray-100 focus:ring-blue-400'
                        : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500'
                      }
                      border rounded-lg 
                      focus:outline-none focus:ring-2 
                      transition-colors duration-300
                    `}
                  >
                    <option value="">Select Sub Category</option>
                    {subCategoriesData?.getAllSubCategories?.map(subCategory => (
                      <option key={subCategory.id} value={subCategory.id}>
                        {subCategory.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`
                    block text-sm font-medium mb-2
                    ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
                  `}>
                    Level
                  </label>
                  <select
                    value={exerciseData.levelId}
                    onChange={(e) => setExerciseData(prev => ({ ...prev, levelId: e.target.value }))}
                    className={`
                      w-full px-4 py-2.5 
                      ${isDarkMode
                        ? 'bg-gray-700 border-gray-600 text-gray-100 focus:ring-blue-400'
                        : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500'
                      }
                      border rounded-lg 
                      focus:outline-none focus:ring-2 
                      transition-colors duration-300
                    `}
                  >
                    <option value="">Select Level</option>
                    {levelsData?.getAllLevels?.map(level => (
                      <option key={level.id} value={level.id}>
                        {level.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className={`
                    block text-sm font-medium mb-2
                    ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
                  `}>
                    Length (Optional)
                  </label>
                  <input
                    type="text"
                    value={exerciseData.length || ''}
                    onChange={(e) => setExerciseData(prev => ({ ...prev, length: e.target.value }))}
                    placeholder="Enter exercise length"
                    className={`
                      w-full px-4 py-2.5 
                      ${isDarkMode
                        ? 'bg-gray-700 border-gray-600 text-gray-100 focus:ring-blue-400'
                        : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500'
                      }
                      border rounded-lg 
                      focus:outline-none focus:ring-2 
                      transition-colors duration-300
                    `}
                  />
                </div>

                <div>
                  <label className={`
                    block text-sm font-medium mb-2
                    ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
                  `}>
                    Tags (Optional)
                  </label>
                  <input
                    type="text"
                    value={exerciseData.tags || ''}
                    onChange={(e) => setExerciseData(prev => ({ ...prev, tags: e.target.value }))}
                    placeholder="Enter tags"
                    className={`
                      w-full px-4 py-2.5 
                      ${isDarkMode
                        ? 'bg-gray-700 border-gray-600 text-gray-100 focus:ring-blue-400'
                        : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500'
                      }
                      border rounded-lg 
                      focus:outline-none focus:ring-2 
                      transition-colors duration-300
                    `}
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className={`
                    block text-sm font-medium mb-2
                    ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
                  `}>
                    Description (Optional)
                  </label>
                  <textarea
                    value={exerciseData.description || ''}
                    onChange={(e) => setExerciseData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Enter exercise description"
                    rows={3}
                    className={`
                      w-full px-4 py-2.5 
                      ${isDarkMode
                        ? 'bg-gray-700 border-gray-600 text-gray-100 focus:ring-blue-400'
                        : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500'
                      }
                      border rounded-lg 
                      focus:outline-none focus:ring-2 
                      transition-colors duration-300
                    `}
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className={`
                    block text-sm font-medium mb-2
                    ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
                  `}>
                    Image Upload
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setImageFile(e.target.files[0])}
                    className={`
                      w-full px-4 py-2.5 
                      ${isDarkMode
                        ? 'bg-gray-700 text-gray-100 border-gray-600'
                        : 'bg-white text-gray-900 border-gray-300'
                      }
                      border rounded-md 
                    `}
                  />
                  {imageFile && (
                    <p className="text-sm text-gray-500 mt-2">
                      Selected: {imageFile.name}
                    </p>
                  )}
                </div>
                <div>
                  <label className={`
                    block text-sm font-medium mb-2
                    ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
                  `}>
                    Video Upload
                  </label>
                  <input
                    type="file"
                    accept="video/*"
                    onChange={(e) => setVideoFile(e.target.files[0])}
                    className={`
                      w-full px-4 py-2.5 
                      ${isDarkMode
                        ? 'bg-gray-700 text-gray-100 border-gray-600'
                        : 'bg-white text-gray-900 border-gray-300'
                      }
                      border rounded-md 
                    `}
                  />
                  {videoFile && (
                    <p className="text-sm text-gray-500 mt-2">
                      Selected: {videoFile.name}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className={`
                    block text-sm font-medium mb-2
                    ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
                  `}>
                    Trending
                  </label>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={exerciseData.trending}
                      onChange={(e) => setExerciseData(prev => ({ ...prev, trending: e.target.checked }))}
                      className={`
                        mr-2 
                        ${isDarkMode
                          ? 'bg-gray-700 border-gray-600 text-blue-500'
                          : 'bg-white border-gray-300 text-blue-500'
                        }
                        rounded focus:ring-blue-500
                      `}
                    />
                    <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Mark as Trending
                    </span>
                  </div>
                </div>

                <div>
                  <label className={`
                    block text-sm font-medium mb-2
                    ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
                  `}>
                    For You
                  </label>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={exerciseData.forYou}
                      onChange={(e) => setExerciseData(prev => ({ ...prev, forYou: e.target.checked }))}
                      className={`
                        mr-2 
                        ${isDarkMode
                          ? 'bg-gray-700 border-gray-600 text-blue-500'
                          : 'bg-white border-gray-300 text-blue-500'
                        }
                        rounded focus:ring-blue-500
                      `}
                    />
                    <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Mark as For You
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={closeCreateModal}
                  className={`
                    px-6 py-2.5 rounded-lg
                    ${isDarkMode
                      ? 'text-gray-300 bg-gray-700 hover:bg-gray-600'
                      : 'text-gray-600 bg-gray-100 hover:bg-gray-200'
                    }
                    transition-colors duration-300
                  `}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`
                    inline-flex items-center justify-center 
                    px-6 py-2.5 rounded-lg text-white font-semibold
                    transition-all duration-300
                    ${editingExercise
                      ? (isDarkMode
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-green-500 hover:bg-green-600')
                      : (isDarkMode
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : 'bg-blue-500 hover:bg-blue-600')
                    }
                  `}
                >
                  {editingExercise ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Exercises List Card */}
      <div className={`
        ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}
        shadow-lg rounded-xl border 
        transition-colors duration-300
      `}>
        {/* Card Header */}
        <div className={`
          px-6 py-4 
          ${isDarkMode ? 'border-b-gray-700' : 'border-b-gray-200'}
          border-b 
          flex justify-between items-center
        `}>
          <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
            Exercises List
          </h2>
          <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Total: {data?.getAllExercises?.total || 0} exercises
          </div>
        </div>

        {/* Exercises Table */}
        <div className="overflow-x-auto">
          {data?.getAllExercises?.exercises?.length === 0 ? (
            <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              No exercises found
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className={`
                  ${isDarkMode
                    ? 'bg-gray-700 text-gray-300'
                    : 'bg-gray-100 text-gray-600'
                  } 
                  text-sm
                `}>
                  <th className="px-6 py-3 text-left">Video</th>
                  <th className="px-6 py-3 text-left">Exercise Name</th>
                  <th className="px-6 py-3 text-left">Description</th>
                  <th className="px-6 py-3 text-left">Level</th>
                  <th className="px-6 py-3 text-left">Trending</th>
                  <th className="px-6 py-3 text-left">For You</th>
                  <th className="px-6 py-3 text-left">Created At</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.getAllExercises.exercises.map((exercise) => (
                  <tr
                    key={exercise.id}
                    className={`
                      border-b 
                      ${isDarkMode
                        ? 'border-gray-700 hover:bg-gray-700'
                        : 'border-gray-200 hover:bg-gray-50'
                      }
                      transition duration-150
                    `}
                  >
                    <td className="px-6 py-4">
                      {exercise.videoUrl ? (
                        <div
                          className="relative w-16 h-16 cursor-pointer group"
                          onClick={() => handleVideoClick(exercise.videoUrl)}
                        >
                          <div className="absolute inset-0 bg-black bg-opacity-30 
                            flex items-center justify-center opacity-0 
                            group-hover:opacity-100 transition-opacity duration-300 
                            rounded-md"
                          >
                            <PlayIcon className="w-8 h-8 text-white" />
                          </div>
                          {exercise.imageUrl ? (
                            <img
                              src={exercise.imageUrl}
                              alt={`${exercise.name} thumbnail`}
                              className="w-16 h-16 object-cover rounded-md"
                            />
                          ) : (
                            <div className={`
                              w-16 h-16 flex items-center justify-center 
                              rounded-md 
                              ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}
                            `}>
                              <span className={`
                                text-xs 
                                ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}
                              `}>
                                No Image
                              </span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className={`
                          w-16 h-16 flex items-center justify-center 
                          rounded-md 
                          ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}
                        `}>
                          <span className={`
                            text-xs 
                            ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}
                          `}>
                            No Video
                          </span>
                        </div>
                      )}
                    </td>
                    <td className={`px-6 py-4 font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      {exercise.name}
                    </td>
                    <td className={`px-6 py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} max-w-xs truncate`} title={exercise.description}>
                      {exercise.description || 'N/A'}
                    </td>
                    <td className={`px-6 py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {exercise.level?.name || 'N/A'}
                    </td>
                    <td className={`px-6 py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {exercise.trending ? 'Yes' : 'No'}
                    </td>
                    <td className={`px-6 py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {exercise.forYou ? 'Yes' : 'No'}
                    </td>
                    <td className={`px-6 py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {formatDate(exercise.createdAt)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleEdit(exercise)}
                          className={`
                            p-2 rounded-full 
                            ${isDarkMode
                              ? 'text-blue-400 hover:text-blue-300 hover:bg-blue-700'
                              : 'text-blue-500 hover:text-blue-700 hover:bg-blue-100'
                            }
                            transition
                          `}
                          title="Edit"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(exercise.id)}
                          className={`
                            p-2 rounded-full 
                            ${isDarkMode
                              ? 'text-red-400 hover:text-red-300 hover:bg-red-700'
                              : 'text-red-500 hover:text-red-700 hover:bg-red-100'
                            }
                            transition
                          `}
                          title="Delete"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        <div className="flex justify-between items-center mt-4">
          <div className="text-sm text-gray-600">
            Page {data?.getAllExercises?.page || 1} of {data?.getAllExercises?.totalPages || 1}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={handlePrevPage}
              disabled={page === 1}
              className={`
                px-4 py-2 rounded
                ${page === 1
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : `
                    ${isDarkMode
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }
                  `
                }
              `}
            >
              Previous
            </button>
            <button
              onClick={handleNextPage}
              disabled={page === (data?.getAllExercises?.totalPages || 1)}
              className={`
                px-4 py-2 rounded
                ${page === (data?.getAllExercises?.totalPages || 1)
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : `
                    ${isDarkMode
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }
                  `
                }
              `}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {selectedVideo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center 
          bg-black bg-opacity-70 p-4"
          onClick={closeVideoModal}
        >
          <div
            className="relative max-w-4xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeVideoModal}
              className="absolute -top-10 right-0 text-white 
              hover:text-gray-300 transition duration-300"
            >
              Close (X)
            </button>
            <video
              src={selectedVideo}
              controls
              autoPlay
              className="w-full rounded-lg shadow-2xl"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ExercisesManagement;
